/* MarkFittedDialog — doctor-side "patient is fitted" confirmation.
 *
 * Used on the doctor's Case Detail page when the case is in
 * `in_treatment` status but `current_aligner_stage` is still 0 — i.e.
 * the package arrived at the clinic but the doctor hasn't formally
 * transitioned the patient to "wearing Aligner 1" yet.
 *
 * Why this matters: the `aligner_stage_updated_at` field set by this
 * action is the day-1 anchor used by /patient/me/aligner-progress for
 * day-of-tray + next-switch-ETA. Setting it to "today" when the actual
 * fitting was 4 days ago would mis-anchor the patient's whole
 * dashboard. Hence the explicit date picker.
 *
 * Props:
 *   - open / onOpenChange — controlled visibility
 *   - caseId — passed to POST /cases/{caseId}/mark-fitted
 *   - patientName — friendly heading copy
 *   - onSuccess — called with the updated case after success
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Tooth, Calendar as CalendarIcon } from "@phosphor-icons/react";


export default function MarkFittedDialog({ open, onOpenChange, caseId, patientName, onSuccess }) {
    const [fittedAt, setFittedAt] = useState(() => new Date().toISOString().slice(0, 10));
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setFittedAt(new Date().toISOString().slice(0, 10));
            setNote("");
        }
    }, [open]);

    const todayIso = new Date().toISOString().slice(0, 10);

    const submit = async () => {
        if (!fittedAt) {
            toast.error("Pick the fitting date");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post(`/cases/${caseId}/mark-fitted`, {
                fitted_at: fittedAt,
                note: note.trim() || null,
            });
            toast.success(`${patientName || "Patient"} marked as fitted — Aligner 1 active.`);
            onSuccess?.(data);
            onOpenChange(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't mark as fitted");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
            <DialogContent className="max-w-md" data-testid="mark-fitted-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Tooth size={18} weight="duotone" className="text-primary" />
                        Mark patient as fitted
                    </DialogTitle>
                    <DialogDescription>
                        Confirms <strong>{patientName || "the patient"}</strong> received and started wearing
                        Aligner&nbsp;1. This becomes Day&nbsp;1 of their treatment — the date you pick is what
                        their dashboard will count from.
                    </DialogDescription>
                </DialogHeader>

                <div className="mt-3 space-y-4">
                    <div>
                        <Label htmlFor="fitted-at-input" className="text-xs font-semibold flex items-center gap-1.5">
                            <CalendarIcon size={12} weight="bold" /> Fitting date
                        </Label>
                        <Input
                            id="fitted-at-input"
                            type="date"
                            value={fittedAt}
                            max={todayIso}
                            onChange={(e) => setFittedAt(e.target.value)}
                            className="h-10 mt-1"
                            data-testid="mark-fitted-date-input"
                        />
                        <p className="text-[11px] text-muted-foreground mt-1.5">
                            Default is today. Back-date if the fitting appointment was earlier — the patient's
                            day-of-tray counter and next-switch ETA depend on this date being accurate.
                        </p>
                    </div>

                    <div>
                        <Label htmlFor="fitted-note-input" className="text-xs font-semibold">
                            Note <span className="text-muted-foreground font-normal">(optional, internal)</span>
                        </Label>
                        <Textarea
                            id="fitted-note-input"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            rows={2}
                            placeholder="e.g. attachments bonded successfully; reviewed care card with patient."
                            className="mt-1"
                            data-testid="mark-fitted-note-input"
                            maxLength={500}
                        />
                    </div>
                </div>

                <DialogFooter className="mt-4">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
                    <Button onClick={submit} disabled={saving || !fittedAt} data-testid="mark-fitted-submit">
                        {saving ? "Saving…" : "Mark fitted"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
